/*
  Dot page indicators for the mobile product rows (VT-19b, 2026-08-29).

  Renders nothing itself — snippets/slider-buttons.liquid emits the dots server-side with the
  first one active, so there is no layout shift when this upgrades. All this does is track the
  sibling slider's scroll position and move the active class, plus update a visually-hidden
  aria-live status so screen readers hear the page change that sighted users see.

  Indicators only: swipe is the navigation below 990, and the dots are aria-hidden. Nothing in
  global.js is touched — SliderComponent stays the owner of the arrows and its own paging.

  The element is created fresh whenever product-recommendations swaps its innerHTML, so all
  wiring lives in connectedCallback and is torn down in disconnectedCallback.
*/
if (!customElements.get('slider-page-dots')) {
  class SliderPageDots extends HTMLElement {
    #controller = null;
    #frame = 0;

    connectedCallback() {
      this.slider = this.closest('slider-component')?.querySelector('ul.slider');
      this.dots = Array.from(this.querySelectorAll('.slider-dots__dot'));
      this.status = this.querySelector('[data-dots-current]');
      if (!this.slider || this.dots.length < 2) return;

      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.slider.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      window.addEventListener('resize', this.#onScroll, { passive: true, signal });
      this.#update();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#controller?.abort();
      this.#controller = null;
    }

    #onScroll = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#update();
      });
    };

    /**
     * Maps scroll position onto the dots by proportion of the scrollable range rather than by
     * counting slides: the rows carry a peek gutter and a trailing spacer, so slide widths do
     * not divide the track evenly, and proportional mapping still lands exactly on the last dot
     * at the end of the scroll.
     */
    #update() {
      const lastIndex = this.dots.length - 1;
      const scrollable = this.slider.scrollWidth - this.slider.clientWidth;
      const ratio = scrollable > 0 ? this.slider.scrollLeft / scrollable : 0;
      const index = Math.min(lastIndex, Math.max(0, Math.round(ratio * lastIndex)));

      for (const [i, dot] of this.dots.entries()) {
        dot.classList.toggle('slider-dots__dot--active', i === index);
      }

      if (this.status) this.status.textContent = `${index + 1}`;
    }
  }

  customElements.define('slider-page-dots', SliderPageDots);
}
